// webaudiodev.ts ↔ game/sdl/sdlsounddev.cpp (the WEB arm) — the SoundDevice
// over Web Audio. The SDL device pull-mixed ADPCM through a U8/8000Hz/mono
// callback (sdlsounddev.cpp:123-128); here each PlayAdpcm decodes the sound
// once (mixer.ts, cached per byte-buffer) into an 8000Hz AudioBuffer and
// schedules it on the channel's slot through a master GainNode — the browser's
// graph does the mixing the C++ MixChannels did.
//
// AUTOPLAY/SAFARI UNLOCK (the wi-ios-audio-fix pattern, sdlsounddev.cpp:108-119
// + main.ts wireAudioUnlock): the AudioContext is NOT created until unlock() —
// the shell (main.ts) calls unlock() from user-gesture handlers (touchend/
// click/keydown/pointerup), on visibilitychange→visible and pageshow, because
// modern WebKit doesn't treat touchstart as user activation, DESKTOP Safari
// starts the context 'suspended' and can re-suspend it on focus loss, and iOS
// suspends the context across interruptions. unlock() also marks the page's audio as
// primary media (navigator.audioSession.type = 'playback', Safari 17+) so the
// iOS silent switch doesn't mute it. Before the first unlock, plays are
// dropped — exactly the C++ web behavior before its first-gesture
// SDL_OpenAudio.

import { decodeAdpcm } from './mixer.ts';
import type { SoundDevice } from './sounddevice.ts';

// sdlsounddev.cpp:13-23
export const kcChannels = 4;
const kcBuffers = 4;
const kcbBuffer = 1024; // the WEB arm (:18-20 — smaller clicks on iPhone)
export const knRateSamples = 8000; // m_spec.freq (:123)

// The minimal structural slice of the Web Audio API the device touches —
// injectable so node tests run against a stub (no DOM lib dependency).
export interface AudioBufferLike {
  readonly length: number;
  readonly sampleRate: number;
  getChannelData(c: number): Float32Array;
}
export interface AudioNodeLike {
  connect(dest: unknown): unknown;
  disconnect?(): void;
}
export interface GainNodeLike extends AudioNodeLike {
  gain: { value: number };
}
export interface BufferSourceLike extends AudioNodeLike {
  buffer: AudioBufferLike | null;
  start(when?: number): void;
  stop(when?: number): void;
  onended: (() => void) | null;
}
export interface AudioContextLike {
  readonly currentTime: number; // seconds
  readonly state: string; // 'suspended' | 'running' | 'closed'
  readonly destination: unknown;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  close(): Promise<void>;
  createGain(): GainNodeLike;
  createBufferSource(): BufferSourceLike;
  createBuffer(cChannels: number, length: number, rate: number): AudioBufferLike;
}

/** One playing slot ↔ mixer.h:8 Channel (the decoded source + its end time
 *  stand in for the pb/pbEnd cursor the SDL mixer drained in real time). */
interface WebChannel {
  source: BufferSourceLike | null;
  tEnd: number; // ctx.currentTime seconds when the buffer drains
}

/** Decode + convert: 4-bit ADPCM → 8-bit unsigned PCM (mixer.ts) → float
 *  [-1, 1) the way the U8 wave maps (sample - 128) / 128. */
export function adpcmToFloat(pb: Uint8Array): Float32Array {
  const pcm = decodeAdpcm(pb);
  const f = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) f[i] = (pcm[i] - 128) / 128;
  return f;
}

export class WebAudioSoundDevice implements SoundDevice {
  // ↔ SdlSoundDevice
  // sdlsounddev.cpp:69-76 SdlSoundDevice::SdlSoundDevice
  private m_fEnable = false;
  private m_tSilence = 0; // seconds of ctx.currentTime; 0 = silent (:75)
  private m_achnl: WebChannel[] = [];
  // :240-250 — the SDL stub had no volume control and reported 255; this
  // device HAS one (the IPHONE AudioQueue arm's nVolume/255 gain, :234-237),
  // initialized to 255 so pre-SetVolume behavior matches the stub.
  private m_nVolume = 255;

  /** Buffers actually scheduled on a channel (post-unlock plays) — debug/
   *  verification instrumentation (the wiTs handle reads it). */
  cPlays = 0;

  private ctx: AudioContextLike | null = null;
  private gainMaster: GainNodeLike | null = null;
  private bufferCache = new WeakMap<Uint8Array, AudioBufferLike>();
  private readonly createContext: () => AudioContextLike | null;

  /** createContext defaults to the browser AudioContext; inject a stub for
   *  tests or a prefixed implementation for old WebKit. */
  constructor(createContext?: () => AudioContextLike | null) {
    for (let i = 0; i < kcChannels; i++) {
      this.m_achnl.push({ source: null, tEnd: 0 });
    }
    this.createContext =
      createContext ??
      (() => {
        const g = globalThis as unknown as { AudioContext?: new () => AudioContextLike };
        return g.AudioContext ? new g.AudioContext() : null;
      });
  }

  /** The user-gesture hook ↔ sdlsounddev.cpp:89-105 onBrowserEvent (deferred
   *  SDL_OpenAudio) + the shell's resumeAudio (index.html): create the context
   *  on the first gesture, resume it whenever WebKit suspended it (gesture,
   *  tab return, iOS interruption end). Idempotent — wire it to touchend,
   *  click, keydown, visibilitychange→visible and pageshow.
   *
   *  DESKTOP SAFARI (and WebKit generally): an AudioContext is created
   *  'suspended' and only reliably transitions to 'running' when resume() is
   *  called SYNCHRONOUSLY inside a user-gesture handler — main.ts wires unlock()
   *  to click/keydown/pointerup, which all fire on desktop, so we are inside the
   *  gesture here. We must NOT de-register after one call: Safari often resolves
   *  the FIRST resume() into a still-suspended state (the activation isn't yet
   *  "sticky"), and it re-suspends on focus loss / inactivity. The page-life
   *  listeners (never removed) keep retrying every gesture until state ===
   *  'running'. We also play a 1-sample silent buffer through the master gain
   *  the first time the context exists: WebKit needs the output graph exercised
   *  once from a gesture or the destination can stay effectively muted even
   *  after resume() resolves. */
  unlock(): void {
    const justCreated = this.ctx === null;
    if (this.ctx === null) {
      // Audio Session API (Safari 17+): primary media ⇒ silent-switch playback
      const nav = (
        globalThis as unknown as {
          navigator?: { audioSession?: { type: string } };
        }
      ).navigator;
      if (nav?.audioSession) {
        try {
          nav.audioSession.type = 'playback';
        } catch {
          /* ignore */
        }
      }
      this.ctx = this.createContext();
      if (this.ctx === null) return;
      this.gainMaster = this.ctx.createGain();
      this.gainMaster.gain.value = this.m_nVolume / 255;
      this.gainMaster.connect(this.ctx.destination);
      this.primeOutput(); // WebKit: exercise the graph once from the gesture
    }
    if (this.ctx.state !== 'running') {
      // Called synchronously inside the gesture handler (the only place Safari
      // honors it). Keep retrying on every subsequent gesture — do NOT assume
      // one resume() reaches 'running'.
      this.ctx.resume().catch(() => {
        /* needs a real gesture; retried */
      });
      // Re-exercise the graph from THIS gesture: WebKit can leave the output muted after a
      // re-suspend (focus loss / tab switch) even once resume() is called, unless a buffer
      // source is start()ed from a real gesture — the same reason primeOutput() runs at
      // creation. Re-prime only when the context ALREADY existed and slipped back to
      // 'suspended' (the creation path above already primed once).
      if (!justCreated) this.primeOutput();
    }
  }

  /** Play a single 1-sample silent buffer through the master gain. The
   *  canonical WebKit "unlock" primer: Safari can leave the output muted after
   *  resume() unless a buffer source has been start()ed from a real gesture.
   *  Cheap + inaudible; runs once per context (right after creation in unlock()). */
  private primeOutput(): void {
    if (this.ctx === null || this.gainMaster === null) return;
    try {
      const buf = this.ctx.createBuffer(1, 1, knRateSamples); // 1 silent sample
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gainMaster);
      src.start();
    } catch {
      /* ignore — best-effort primer */
    }
  }

  /** Whether unlock() has produced a context (the shell can stop listening). */
  get unlocked(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Page-hide teardown (issue #110): a covered/backgrounded iPad keeps the
   *  AudioContext running (audibly) well past the cover closing — the
   *  browser's own auto-suspend is late and inconsistent — so the shell
   *  calls this on visibilitychange→hidden / pagehide when no MP game needs
   *  the audio. Independent of m_fEnable (the user's sound option, not page
   *  lifecycle).
   *
   *  The context is CLOSED outright, not suspend()ed: iOS hands a
   *  page-suspended context back as a ZOMBIE after the PWA returns to the
   *  foreground — state reads 'running' (or the non-standard 'interrupted')
   *  while the output stays dead, so the gesture-wired unlock() sees nothing
   *  to fix and the game stays MUTE. Closing means the next unlock()
   *  (visibilitychange→visible or any gesture) re-opens a FRESH context —
   *  the exact path that works at every app launch (RestoreState semantics;
   *  the decode cache rebuilds lazily). Channels are stopped first so
   *  CoreAudio's ramp-down latches silence, not a mid-buffer quantum (the
   *  PWA-close fade-out tone). */
  teardownHidden(): void {
    if (this.ctx === null) return;
    this.dispose();
    this.m_tSilence = 0;
  }

  // sdlsounddev.cpp:144-147
  IsEnabled(): boolean {
    return this.m_fEnable;
  }

  // sdlsounddev.cpp:149-175 SdlSoundDevice::Enable — both arms reset the
  // channels + the silence clock; SDL_PauseAudio maps to resume/suspend.
  Enable(fEnable: boolean): void {
    if (fEnable) {
      if (!this.m_fEnable) {
        this.resetChannels(); // memset(m_achnl, 0, ...)
        this.m_tSilence = 0;
        this.m_fEnable = true;
        if (this.ctx && this.ctx.state !== 'running') {
          this.ctx.resume().catch(() => {
            /* gesture-gated */
          });
        }
      }
    } else {
      if (this.m_fEnable) {
        this.m_fEnable = false;
        this.resetChannels();
        this.m_tSilence = 0;
        if (this.ctx && this.ctx.state === 'running') {
          this.ctx.suspend().catch(() => {
            /* ignore */
          });
        }
      }
    }
  }

  // sdlsounddev.cpp:177-180
  GetChannelCount(): number {
    return kcChannels;
  }

  // sdlsounddev.cpp:182-188 — free once the cursor passed the end; here, once
  // the scheduled buffer drained (or was never scheduled).
  IsChannelFree(ichnl: number): boolean {
    if (ichnl < 0 || ichnl >= kcChannels) return false;
    const pchnl = this.m_achnl[ichnl];
    if (pchnl.source === null) return true;
    return this.ctx !== null && this.ctx.currentTime >= pchnl.tEnd;
  }

  // sdlsounddev.cpp:190-200
  IsSilent(): boolean {
    if (!this.m_fEnable) return true;
    if (this.m_tSilence === 0) return true;
    if (this.ctx === null) return true;
    return this.ctx.currentTime >= this.m_tSilence;
  }

  // sdlsounddev.cpp:202-204
  ServiceProc(): void {}

  // sdlsounddev.cpp:206-226 SdlSoundDevice::PlayAdpcm
  PlayAdpcm(ichnl: number, pb: Uint8Array): void {
    if (ichnl < 0 || ichnl >= kcChannels) return;
    if (!this.m_fEnable) return;
    // pre-unlock: no context yet — dropped, like the C++ web arm before its
    // first-gesture SDL_OpenAudio (:111-119)
    if (this.ctx === null || this.gainMaster === null) return;

    // DESKTOP SAFARI / WebKit: the context can slip back to 'suspended' after
    // unlock() (focus loss, tab switch, inactivity) even though m_fEnable is
    // still set. A one-shot buffer scheduled into a suspended graph is silently
    // consumed — currentTime doesn't advance, and the sound is gone for good
    // when the context later resumes. Re-resume here so this play (and the rest
    // of the session) is audible. We're outside a gesture, so Safari may not
    // honor it immediately, but the gesture-wired unlock() retries cover that;
    // this catches the common case where a gesture already happened and the
    // context only re-suspended.
    if (this.ctx.state !== 'running') {
      this.ctx.resume().catch(() => {
        /* gesture-gated; unlock() retries */
      });
    }

    // Start it up (:215-225) — replacing the channel's playing sound
    const pchnl = this.m_achnl[ichnl];
    if (pchnl.source !== null) {
      try {
        pchnl.source.stop();
      } catch {
        /* already ended */
      }
      pchnl.source.onended = null;
    }
    const buf = this.getBuffer(pb);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.gainMaster);
    pchnl.source = src;
    pchnl.tEnd = this.ctx.currentTime + buf.length / knRateSamples;
    src.onended = () => {
      if (pchnl.source === src) pchnl.source = null;
    };
    src.start();
    this.cPlays++;

    // :222-223 m_tSilence = now + (cb + kcBuffers*size + 39)/40 + 1 ticks —
    // the same maths in seconds (40 ADPCM bytes/tick, 100 ticks/s).
    this.m_tSilence =
      this.ctx.currentTime + (Math.floor((pb.length + kcBuffers * kcbBuffer + 39) / 40) + 1) / 100;
  }

  // sdlsounddev.cpp:228-238 SdlSoundDevice::SetVolume — clamp 0-255; the gain
  // mapping is the IPHONE arm's nVolume/255 (:235-236), which SDL stubbed out.
  SetVolume(nVolume: number): void {
    if (nVolume < 0) nVolume = 0;
    if (nVolume > 255) nVolume = 255;
    this.m_nVolume = nVolume;
    if (this.gainMaster !== null) {
      this.gainMaster.gain.value = nVolume / 255;
    }
  }

  // sdlsounddev.cpp:240-250 SdlSoundDevice::GetVolume — the stored volume
  // (the SDL stub's constant 255 is this device's initial value).
  GetVolume(): number {
    return this.m_nVolume;
  }

  // sdlsounddev.cpp:78-83 ~SdlSoundDevice (SDL_CloseAudio)
  dispose(): void {
    this.resetChannels();
    if (this.ctx !== null) {
      this.ctx.close().catch(() => {
        /* ignore */
      });
      this.ctx = null;
      this.gainMaster = null;
      // drop buffers minted by the closed context — a re-opened context
      // (unlock() after teardownHidden) decodes fresh ones lazily
      this.bufferCache = new WeakMap();
    }
  }

  /** memset(m_achnl, 0, sizeof(m_achnl)) (:155/:168) — stop + clear slots. */
  private resetChannels(): void {
    for (const pchnl of this.m_achnl) {
      if (pchnl.source !== null) {
        try {
          pchnl.source.stop();
        } catch {
          /* already ended */
        }
        pchnl.source.onended = null;
        pchnl.source = null;
      }
      pchnl.tEnd = 0;
    }
  }

  /** Decode-once cache: PcmHeader.pb buffers are stable for the pack's life,
   *  so the WeakMap keys on object identity. */
  private getBuffer(pb: Uint8Array): AudioBufferLike {
    let buf = this.bufferCache.get(pb);
    if (buf === undefined) {
      const f = adpcmToFloat(pb);
      buf = this.ctx!.createBuffer(1, f.length, knRateSamples);
      buf.getChannelData(0).set(f);
      this.bufferCache.set(pb, buf);
    }
    return buf;
  }
}
